import { useCallback, useRef } from "react";
import { getConnectedEdges, getIncomers, getOutgoers } from "@xyflow/react";
import nodeTypesConfig from "../../models/nodeTypesConfig";

export default function useNodeManager(
  nodes,
  edges,
  setNodes,
  setEdges,
  generateId
) {
  const apiTriggerEligibleTypes = new Set([
    "smsMessage",
    "whatsappMessage",
    "emailMessage",
    "sendToCrm",
  ]);
  const conditionLikeNodeTypes = new Set([
    "conditionNode",
    "customNodeCommand",
  ]);
  // Use ref to track positioning adjustments to avoid stale closures
  const positionAdjustmentTimeouts = useRef(new Set());

  // Helper function to get a node by ID
  const getNode = useCallback(
    (nodeId) => {
      return nodes.find((node) => node.id === nodeId);
    },
    [nodes]
  );

  // Helper function to get an edge by ID
  const getEdge = useCallback(
    (edgeId) => {
      return edges.find((edge) => edge.id === edgeId);
    },
    [edges]
  );

  // Helper function to add nodes
  const addNodes = useCallback(
    (newNodes) => {
      setNodes((currentNodes) => [...currentNodes, ...newNodes]);
    },
    [setNodes]
  );

  // Helper function to add edges
  const addEdges = useCallback(
    (newEdges) => {
      setEdges((currentEdges) => [...currentEdges, ...newEdges]);
    },
    [setEdges]
  );

  // Helper function to delete elements
  const deleteElements = useCallback(
    ({ nodes: nodesToDelete = [], edges: edgesToDelete = [] }) => {
      const nodeIdsToDelete = nodesToDelete.map((n) =>
        typeof n === "string" ? n : n.id
      );
      const edgeIdsToDelete = edgesToDelete.map((e) =>
        typeof e === "string" ? e : e.id
      );

      if (nodeIdsToDelete.length > 0) {
        setNodes((currentNodes) =>
          currentNodes.filter((node) => !nodeIdsToDelete.includes(node.id))
        );
      }

      if (edgeIdsToDelete.length > 0) {
        setEdges((currentEdges) =>
          currentEdges.filter((edge) => !edgeIdsToDelete.includes(edge.id))
        );
      }
    },
    [setNodes, setEdges]
  );
  // In the addNodeBetweenNodes function, update this section:

  const addNodeBetweenNodes = useCallback(
    (type, data = {}, edgeId) => {
      const edge = getEdge(edgeId);

      if (!edge) {
        console.error(`Edge with id ${edgeId} not found.`);
        return;
      }

      const sourceNode = getNode(edge.source);
      const targetNode = getNode(edge.target);

      if (!sourceNode || !targetNode) {
        console.error(`Source or target node not found for edge ${edgeId}.`);
        return;
      }

      if (sourceNode.type === "sqlScheduleTrigger" && type !== "sqlQueryAction") {
        return;
      }

      // Remove the old edge
      deleteElements({ edges: [edgeId] });

      const newNodeId = generateId(type, nodes);

      // Determine edge types based on parent node
      const isParentConditionNode = conditionLikeNodeTypes.has(sourceNode.type);
      const parentEdgeType = isParentConditionNode
        ? "conditionEdge"
        : "actionEdge";
      const parentSourceHandle = isParentConditionNode
        ? edge.sourceHandle
        : undefined;

      // Create the new node
      const newNode = {
        id: newNodeId,
        type,
        data: {
          label:
            data.label ||
            nodeTypesConfig.actions.find((n) => n.type === type)?.displayName ||
            "New Node",
          ...(apiTriggerEligibleTypes.has(type)
            ? { triggerMode: data.triggerMode || "regular" }
            : {}),
          ...data,
        },
        position: { x: 0, y: 0 }, // Will be positioned after measurement
      };

      // Determine if the new node is a condition node OR customNodeCommand
      const isNewNodeConditionNode = conditionLikeNodeTypes.has(type);

      // Create edges
      const sourceToNewEdge = {
        id: generateId("edge", edges),
        source: sourceNode.id,
        target: newNodeId,
        type: parentEdgeType,
        sourceHandle: parentSourceHandle,
        data: edge.data || {},
      };

      const newToTargetEdge = {
        id: generateId("edge", edges),
        source: newNodeId,
        target: targetNode.id,
        type: isNewNodeConditionNode ? "conditionEdge" : "actionEdge",
        sourceHandle: isNewNodeConditionNode
          ? type === "customNodeCommand"
            ? "match"
            : "yes"
          : undefined,
        data: isNewNodeConditionNode
          ? {
              label: type === "customNodeCommand" ? "Match" : "Yes",
            }
          : {},
      };

      const edgesToAdd = [sourceToNewEdge, newToTargetEdge];
      const nodesToAdd = [newNode];

      // If the new node is a condition node OR customCommandNode, create an exit node
      let exitNodeId = null;
      if (isNewNodeConditionNode) {
        exitNodeId = generateId("exitNode", nodes);
        const exitNode = {
          id: exitNodeId,
          type: "exitNode",
          data: { label: "Exit" },
          deletable: false,
          position: { x: 0, y: 0 }, // Will be positioned after measurement
        };

        const exitEdge = {
          id: generateId("edge", edges),
          source: newNodeId,
          target: exitNodeId,
          sourceHandle: type === "customNodeCommand" ? "nomatch" : "no",
          type: "conditionEdge",
          data: {
            label: type === "customNodeCommand" ? "No Match" : "No",
          },
        };

        nodesToAdd.push(exitNode);
        edgesToAdd.push(exitEdge);
      }

      // Add nodes and edges
      addNodes(nodesToAdd);
      addEdges(edgesToAdd);

      // Schedule position adjustment
      const timeoutId = setTimeout(() => {
        positionAdjustmentTimeouts.current.delete(timeoutId);
        adjustPositionsAfterInsertion(
          newNodeId,
          exitNodeId,
          targetNode.id,
          sourceNode,
          targetNode,
          isParentConditionNode,
          isNewNodeConditionNode,
          edge
        );
      }, 50);

      positionAdjustmentTimeouts.current.add(timeoutId);
    },
    [
      nodes,
      edges,
      getNode,
      getEdge,
      addNodes,
      addEdges,
      deleteElements,
      generateId,
    ]
  );

  const adjustPositionsAfterInsertion = useCallback(
    (
      newNodeId,
      exitNodeId,
      targetNodeId,
      sourceNode,
      targetNode,
      isParentConditionNode,
      isNewNodeConditionNode,
      originalEdge
    ) => {
      setNodes((currentNodes) => {
        const newNodeData = currentNodes.find((n) => n.id === newNodeId);
        const exitNodeData = exitNodeId
          ? currentNodes.find((n) => n.id === exitNodeId)
          : null;
        const targetNodeData = currentNodes.find((n) => n.id === targetNodeId);

        if (!newNodeData) return currentNodes;

        const parentNodeData = currentNodes.find((n) => n.id === sourceNode.id);
        const parentNodeWidth =
          parentNodeData?.measured?.width || parentNodeData?.width;

        if (!parentNodeWidth) {
          // Retry if width not available yet
          const retryTimeoutId = setTimeout(() => {
            positionAdjustmentTimeouts.current.delete(retryTimeoutId);
            adjustPositionsAfterInsertion(
              newNodeId,
              exitNodeId,
              targetNodeId,
              sourceNode,
              targetNode,
              isParentConditionNode,
              isNewNodeConditionNode,
              originalEdge
            );
          }, 50);
          positionAdjustmentTimeouts.current.add(retryTimeoutId);
          return currentNodes;
        }

        // Calculate positions
        let newNodePosition;
        if (targetNodeData?.type !== "exitNode") {
          newNodePosition = {
            x: targetNode.position.x,
            y: targetNode.position.y,
          };
        } else {
          newNodePosition = {
            x: targetNode.position.x - 50,
            y: targetNode.position.y,
          };
        }

        const newNodeWidth =
          newNodeData?.measured?.width || newNodeData?.width || 200;
        const parentCenter = newNodePosition.x + newNodeWidth / 2;

        // Update positions
        return currentNodes.map((node) => {
          if (node.id === newNodeId) {
            return { ...node, position: newNodePosition };
          }

          if (node.id === targetNodeId) {
            if (targetNodeData?.type === "exitNode") {
              const yOffset = isNewNodeConditionNode ? 100 : 150;
              const xOffset = isNewNodeConditionNode ? -175 : -25;
              return {
                ...node,
                position: {
                  x: parentCenter + xOffset,
                  y: newNodePosition.y + yOffset,
                },
              };
            } else {
              const yOffset = isNewNodeConditionNode ? 100 : 150;
              const xOffset = isNewNodeConditionNode ? -350 : 0;
              return {
                ...node,
                position: {
                  x: parentCenter + xOffset,
                  y: newNodePosition.y + yOffset,
                },
              };
            }
          }

          if (exitNodeId && node.id === exitNodeId) {
            const extraSpacing = 50;
            return {
              ...node,
              position: {
                x: newNodePosition.x + newNodeWidth + extraSpacing,
                y: newNodePosition.y + 100,
              },
            };
          }

          return node;
        });
      });

      // Adjust downstream nodes
      setTimeout(() => adjustDownstreamNodes(targetNodeId, 0, 100), 10);
    },
    [setNodes]
  );

  const cleanupOrphanedExitNodes = useCallback(
    (latestEdges = edges) => {
      setNodes((currentNodes) => {
        const exitNodes = currentNodes.filter(
          (node) => node.type === "exitNode"
        );

        const exitNodesToDelete = exitNodes.filter((exitNode) => {
          const hasIncomingEdge = latestEdges.some(
            (edge) => edge.target === exitNode.id
          );
          return !hasIncomingEdge;
        });

        if (exitNodesToDelete.length > 0) {
          return currentNodes.filter(
            (node) =>
              !exitNodesToDelete.some((exitNode) => exitNode.id === node.id)
          );
        } else {
          return currentNodes;
        }
      });
    },
    [edges, setNodes]
  );

  const onNodesDelete = useCallback(
    (deletedNodes) => {
      const newExitNodes = [];
      const newExitEdges = [];
      const exitNodesToDelete = [];

      setEdges((currentEdges) => {
        const updatedEdges = deletedNodes.reduce((acc, node) => {
          const incomers = getIncomers(node, nodes, currentEdges);
          const outgoers = getOutgoers(node, nodes, currentEdges);
          const connectedEdges = getConnectedEdges([node], currentEdges);

          // Remove all edges connected to the deleted node
          const remainingEdges = acc.filter(
            (edge) => !connectedEdges.includes(edge)
          );

          // Check exit nodes that were targets of the deleted node
          outgoers.forEach((outgoingNode) => {
            if (outgoingNode.type === "exitNode") {
              // Check if this exit node has any OTHER incoming edges (not from deleted nodes)
              const otherIncomingEdges = remainingEdges.filter(
                (edge) => edge.target === outgoingNode.id
              );

              // Only delete if no other edges point to it
              if (otherIncomingEdges.length === 0) {
                exitNodesToDelete.push(outgoingNode.id);
              }
            }
          });

          // For each incoming edge, check if we need to create an exit node
          incomers.forEach((sourceNode) => {
            // Find the original edge from source to deleted node
            const originalEdge = currentEdges.find(
              (edge) => edge.source === sourceNode.id && edge.target === node.id
            );

            if (!originalEdge) return;

            // Check if source is a condition or custom node
            const isConditionNode = conditionLikeNodeTypes.has(sourceNode.type);

            // For condition/custom nodes: check if THIS HANDLE has other edges
            // For regular nodes: check if the node has any other edges
            let sourceHandleHasOtherEdges;

            if (isConditionNode && originalEdge.sourceHandle) {
              // Check if this specific handle has other edges in remainingEdges
              sourceHandleHasOtherEdges = remainingEdges.some(
                (edge) =>
                  edge.source === sourceNode.id &&
                  edge.sourceHandle === originalEdge.sourceHandle
              );
            } else {
              // Regular node - check if any other edges exist
              sourceHandleHasOtherEdges = remainingEdges.some(
                (edge) => edge.source === sourceNode.id
              );
            }

            // Only create exit node if this handle has no other outgoing edges
            if (!sourceHandleHasOtherEdges) {
              // Check if this source already has an exit node with the same sourceHandle
              const existingExitEdge = remainingEdges.find(
                (edge) =>
                  edge.source === sourceNode.id &&
                  edge.target.includes("exitNode") &&
                  edge.sourceHandle === originalEdge.sourceHandle
              );

              const newExitEdgeExists = newExitEdges.find(
                (edge) =>
                  edge.source === sourceNode.id &&
                  edge.sourceHandle === originalEdge.sourceHandle
              );

              if (!existingExitEdge && !newExitEdgeExists) {
                // Determine position based on source handle for condition/custom nodes
                const isLeftHandle =
                  originalEdge.sourceHandle === "yes" ||
                  originalEdge.sourceHandle === "match";
                const isRightHandle =
                  originalEdge.sourceHandle === "no" ||
                  originalEdge.sourceHandle === "nomatch";

                let exitNodeX, exitNodeY;

                if (isLeftHandle) {
                  exitNodeX = sourceNode.position.x - 100;
                  exitNodeY = sourceNode.position.y + 100;
                } else if (isRightHandle) {
                  exitNodeX =
                    sourceNode.position.x +
                    (sourceNode.measured?.width || 200) +
                    50;
                  exitNodeY = sourceNode.position.y + 100;
                } else {
                  exitNodeX =
                    sourceNode.position.x +
                    (sourceNode.measured?.width || 200) / 2 -
                    20;
                  exitNodeY = sourceNode.position.y + 150;
                }

                // Create a new exit node
                const exitNodeId = generateId("exitNode", nodes);
                const newExitNode = {
                  id: exitNodeId,
                  type: "exitNode",
                  data: { label: "Exit" },
                  deletable: false,
                  position: {
                    x: exitNodeX,
                    y: exitNodeY,
                  },
                };

                // Create new edge from source to exit node
                const newEdge = {
                  id: generateId("edge", remainingEdges),
                  source: sourceNode.id,
                  target: exitNodeId,
                  type: originalEdge.type,
                  sourceHandle: originalEdge.sourceHandle,
                  data: originalEdge.data || {},
                };

                newExitNodes.push(newExitNode);
                newExitEdges.push(newEdge);
              }
            }
          });

          return remainingEdges;
        }, currentEdges);

        return [...updatedEdges, ...newExitEdges];
      });

      // Update nodes: remove deleted nodes, remove orphaned exit nodes, add new exit nodes
      setNodes((currentNodes) => {
        console.log("Exit nodes to delete:", exitNodesToDelete.length);
        console.log("New exit nodes to add:", newExitNodes.length);

        // Remove deleted nodes and their orphaned exit nodes
        const filtered = currentNodes.filter(
          (node) =>
            !deletedNodes.some((dn) => dn.id === node.id) &&
            !exitNodesToDelete.includes(node.id)
        );

        // Add new exit nodes
        return [...filtered, ...newExitNodes];
      });
    },
    [nodes, setNodes, setEdges, cleanupOrphanedExitNodes, generateId]
  );

  const addNode = useCallback(
    (type, data = {}) => {
      if (type === "sqlScheduleTrigger" && nodes.length > 0) {
        return;
      }

      if (type === "addedToSegment" && data.segment) {
        if (data.useSharedExit) {
          // Create shared exit node
          const exitNodeId = generateId("exitNode", nodes);
          const exitNode = {
            id: exitNodeId,
            type: "exitNode",
            data: { label: "Exit" },
            position: {
              x: 250 + ((data.segment.length - 1) * 300) / 2,
              y: 250,
            },
          };

          // Create segment nodes
          const segmentNodes = data.segment.map((segment, index) => {
            const newNodeId = generateId(type, nodes);
            return {
              id: newNodeId,
              type,
              data: {
                label:
                  data.label ||
                  nodeTypesConfig.triggers.find((n) => n.type === type)
                    ?.displayName ||
                  "New Trigger",
                ...(apiTriggerEligibleTypes.has(type)
                  ? { triggerMode: data.triggerMode || "regular" }
                  : {}),
                ...data,
                segment: [segment],
              },
              position: {
                x: 250 + index * 300,
                y: 50,
              },
            };
          });

          // Add all nodes
          addNodes([...segmentNodes, exitNode]);

          // Create edges to shared exit
          const newEdges = segmentNodes.map((node) => ({
            id: generateId("edge", edges),
            source: node.id,
            target: exitNodeId,
            type: "actionEdge",
          }));

          addEdges(newEdges);

          // Adjust exit node position after measurement
          const timeoutId = setTimeout(() => {
            positionAdjustmentTimeouts.current.delete(timeoutId);
            adjustSharedExitPosition(
              segmentNodes,
              exitNodeId,
              data.segment.length
            );
          }, 50);

          positionAdjustmentTimeouts.current.add(timeoutId);
        } else {
          // Create separate exit nodes for each segment
          const nodesToAdd = [];
          const edgesToAdd = [];

          data.segment.forEach((segment, index) => {
            const newNodeId = generateId(type, nodes);
            const exitNodeId = generateId("exitNode", nodes);
            const nodePosition = { x: 250 + index * 300, y: 50 };

            const newNode = {
              id: newNodeId,
              type,
              data: {
                label:
                  data.label ||
                  nodeTypesConfig.triggers.find((n) => n.type === type)
                    ?.displayName ||
                  "New Trigger",
                ...(apiTriggerEligibleTypes.has(type)
                  ? { triggerMode: data.triggerMode || "regular" }
                  : {}),
                ...data,
                segment: [segment],
              },
              position: nodePosition,
            };

            const exitNode = {
              id: exitNodeId,
              type: "exitNode",
              data: { label: "Exit" },
              position: {
                x: nodePosition.x,
                y: nodePosition.y + 200,
              },
            };

            const edge = {
              id: generateId("edge", edges),
              source: newNodeId,
              target: exitNodeId,
              type: "actionEdge",
            };

            nodesToAdd.push(newNode, exitNode);
            edgesToAdd.push(edge);
          });

          addNodes(nodesToAdd);
          addEdges(edgesToAdd);

          // Adjust positions after measurement
          const timeoutId = setTimeout(() => {
            positionAdjustmentTimeouts.current.delete(timeoutId);
            adjustSegmentExitPositions(nodesToAdd);
          }, 50);

          positionAdjustmentTimeouts.current.add(timeoutId);
        }
      } else {
        // Handle single node creation
        const newNodeId = generateId(type, nodes);
        const exitNodeId = generateId("exitNode", nodes);
        const nodePosition = data.position || { x: 250, y: 50 };

        const newNode = {
          id: newNodeId,
          type,
          data: {
            label:
              data.label ||
              nodeTypesConfig.triggers.find((n) => n.type === type)
                ?.displayName ||
              "New Trigger",
            ...(apiTriggerEligibleTypes.has(type)
              ? { triggerMode: data.triggerMode || "regular" }
              : {}),
            ...data,
          },
          position: nodePosition,
        };

        const exitNode = {
          id: exitNodeId,
          type: "exitNode",
          data: { label: "Exit" },
          position: {
            x: nodePosition.x,
            y: nodePosition.y + 150,
          },
        };

        const edge = {
          id: generateId("edge", edges),
          source: newNodeId,
          target: exitNodeId,
          type: "actionEdge",
        };

        addNodes([newNode, exitNode]);
        addEdges([edge]);

        // Adjust exit position after measurement
        const timeoutId = setTimeout(() => {
          positionAdjustmentTimeouts.current.delete(timeoutId);
          adjustSingleExitPosition(newNodeId, exitNodeId, nodePosition);
        }, 50);

        positionAdjustmentTimeouts.current.add(timeoutId);
      }
    },
    [nodes, edges, addNodes, addEdges, generateId]
  );

  const adjustSharedExitPosition = useCallback(
    (segmentNodes, exitNodeId, segmentLength) => {
      setNodes((currentNodes) => {
        const firstSegmentNode = currentNodes.find(
          (n) => n.id === segmentNodes[0].id
        );

        if (firstSegmentNode?.measured?.width) {
          const nodeWidth = firstSegmentNode.measured.width;
          const totalWidth = (segmentLength - 1) * 300 + nodeWidth;
          const centerX = 250 + totalWidth / 2;
          const exitNodeWidth = 50;

          return currentNodes.map((node) => {
            if (node.id === exitNodeId) {
              return {
                ...node,
                position: {
                  x: centerX - exitNodeWidth / 2,
                  y: 250,
                },
              };
            }
            return node;
          });
        }
        return currentNodes;
      });
    },
    [setNodes]
  );

  const adjustSegmentExitPositions = useCallback(
    (nodesToAdd) => {
      setNodes((currentNodes) => {
        return currentNodes.map((node) => {
          const correspondingAddedNode = nodesToAdd.find(
            (n) => n.id === node.id
          );
          if (correspondingAddedNode && node.type === "exitNode") {
            // Find the parent trigger node
            const parentNodeId = nodesToAdd.find(
              (n) =>
                n.type !== "exitNode" &&
                nodesToAdd.indexOf(correspondingAddedNode) ===
                  nodesToAdd.indexOf(n) + 1
            )?.id;

            const parentNode = currentNodes.find((n) => n.id === parentNodeId);
            if (parentNode?.measured?.width) {
              const parentCenter = Math.round(
                parentNode.position.x + parentNode.measured.width / 2
              );
              const exitNodeWidth = 50;

              return {
                ...node,
                position: {
                  x: parentCenter - exitNodeWidth / 2,
                  y: parentNode.position.y + 200,
                },
              };
            }
          }
          return node;
        });
      });
    },
    [setNodes]
  );

  const adjustSingleExitPosition = useCallback(
    (newNodeId, exitNodeId, nodePosition) => {
      setNodes((currentNodes) => {
        const parentNode = currentNodes.find((n) => n.id === newNodeId);

        if (parentNode?.measured?.width) {
          const parentCenter = Math.round(
            nodePosition.x + parentNode.measured.width / 2
          );
          const exitNodeWidth = 50;

          return currentNodes.map((node) => {
            if (node.id === exitNodeId) {
              return {
                ...node,
                position: {
                  x: parentCenter - exitNodeWidth / 2,
                  y: nodePosition.y + 150,
                },
              };
            }
            return node;
          });
        }
        return currentNodes;
      });
    },
    [setNodes]
  );

  const adjustDownstreamNodes = useCallback(
    (nodeId, deltaX = 0, deltaY = 100) => {
      setNodes((currentNodes) => {
        const adjustRecursively = (id, nodesArray, dx, dy) => {
          const node = nodesArray.find((n) => n.id === id);
          if (!node) return nodesArray;

          const downstreamEdges = edges.filter((e) => e.source === node.id);

          return downstreamEdges.reduce((acc, edge) => {
            const childNodeIndex = acc.findIndex((n) => n.id === edge.target);
            if (childNodeIndex === -1) return acc;

            const childNode = acc[childNodeIndex];

            if (childNode.type === "exitNode") {
              const parentCenter = Math.round(
                node.position.x + (node.measured?.width || 200) / 2
              );
              const exitNodeWidth = 50;
              acc[childNodeIndex] = {
                ...childNode,
                position: {
                  x: parentCenter - exitNodeWidth / 2,
                  y: node.position.y + 150,
                },
              };
            } else {
              acc[childNodeIndex] = {
                ...childNode,
                position: {
                  x: node.position.x + dx,
                  y: node.position.y + dy,
                },
              };
            }

            return adjustRecursively(childNode.id, acc, dx, dy);
          }, nodesArray);
        };

        return adjustRecursively(nodeId, currentNodes, deltaX, deltaY);
      });
    },
    [setNodes, edges]
  );

  const editNode = useCallback(
    (nodeId, newData) => {
      console.log(newData);

      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                ...newData,
              },
            };
          }
          return node;
        })
      );
    },
    [setNodes]
  );

  // Cleanup timeouts on unmount
  const cleanup = useCallback(() => {
    positionAdjustmentTimeouts.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    positionAdjustmentTimeouts.current.clear();
  }, []);

  return {
    addNodeBetweenNodes,
    addNode,
    onNodesDelete,
    editNode,
    cleanupOrphanedExitNodes,
    cleanup, // Expose cleanup for parent component to call on unmount
  };
}
